import { create } from 'zustand';
import { getUnreadCount, getUpcomingReminders, markAllRemindersRead } from '@/lib/reminders-api';
import type { Reminder } from '@/types';

interface RemindersState {
  unreadCount: number;
  upcomingReminders: Reminder[];
  isLoading: boolean;
  lastFetched: number | null;
  pollingInterval: NodeJS.Timeout | null;

  // Actions
  fetchUnreadCount: () => Promise<void>;
  fetchUpcoming: () => Promise<void>;
  refreshAll: () => Promise<void>;
  markAllRead: () => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
  decrementCount: () => void;
}

export const useRemindersStore = create<RemindersState>((set, get) => ({
  unreadCount: 0,
  upcomingReminders: [],
  isLoading: false,
  lastFetched: null,
  pollingInterval: null,

  fetchUnreadCount: async () => {
    try {
      const count = await getUnreadCount();
      set({ unreadCount: count });
    } catch (error) {
      console.error('Failed to fetch unread count', error);
    }
  },

  fetchUpcoming: async () => {
    try {
      const reminders = await getUpcomingReminders(5);
      set({ upcomingReminders: reminders });
    } catch (error) {
      console.error('Failed to fetch upcoming reminders', error);
    }
  },

  refreshAll: async () => {
    set({ isLoading: true });
    try {
      const [count, reminders] = await Promise.all([
        getUnreadCount(),
        getUpcomingReminders(5),
      ]);
      set({
        unreadCount: count,
        upcomingReminders: reminders,
        lastFetched: Date.now(),
      });
    } catch (error) {
      console.error('Failed to refresh reminders', error);
    } finally {
      set({ isLoading: false });
    }
  },

  markAllRead: async () => {
    try {
      await markAllRemindersRead();
      set({ unreadCount: 0, upcomingReminders: [] });
    } catch (error) {
      console.error('Failed to mark all as read', error);
      throw error;
    }
  },

  startPolling: (intervalMs = 10000) => {
    const { pollingInterval, refreshAll } = get();

    // Clear existing interval if any
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Initial fetch
    refreshAll();

    // Start polling
    const interval = setInterval(() => {
      refreshAll();
    }, intervalMs);

    set({ pollingInterval: interval });
  },

  stopPolling: () => {
    const { pollingInterval } = get();
    if (pollingInterval) {
      clearInterval(pollingInterval);
      set({ pollingInterval: null });
    }
  },

  decrementCount: () => {
    set((state) => ({
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },
}));
